<template>
  <div class="video-upload-input">
    <div
      class="video-upload-input__zone"
      @dragover.prevent
      @drop.prevent="onDrop"
    >
      <input
        :id="inputId"
        class="video-upload-input__file"
        type="file"
        :accept="accept"
        :multiple="multiple"
        :disabled="disabled"
        @change="handleFileChange"
      />
      <span class="video-upload-input__icon" aria-hidden="true">
        <fa icon="fa-cloud-arrow-up" />
      </span>
      <span class="video-upload-input__title">{{ placeholder || 'Clique para enviar o vídeo' }}</span>
      <span class="video-upload-input__hint">ou arraste e solte o arquivo aqui</span>
      <span class="video-upload-input__formats">{{ acceptText }}</span>
    </div>

    <div v-if="hasFile" class="video-upload-input__filecard">
      <span class="video-upload-input__thumb" aria-hidden="true">
        <fa icon="fa-circle-play" />
      </span>
      <div class="video-upload-input__meta">
        <strong class="video-upload-input__name">{{ displayName }}</strong>
        <span class="video-upload-input__details">{{ displayMeta }}</span>
        <div v-if="showProgress" class="video-upload-input__progress">
          <div class="video-upload-input__bar">
            <div class="video-upload-input__fill" :style="{ width: progressWidth }"></div>
          </div>
          <span class="video-upload-input__percent">{{ progressPercent }}</span>
        </div>
        <span v-if="isComplete" class="video-upload-input__status video-upload-input__status--done">
          <fa icon="fa-circle-check" />
          Upload concluído
        </span>
        <span v-else-if="uploading" class="video-upload-input__status">Enviando…</span>
        <span v-else class="video-upload-input__status">Selecionado</span>
      </div>
      <button
        class="video-upload-input__remove"
        type="button"
        title="Remover vídeo"
        :disabled="disabled"
        @click="removePreview"
      >
        <fa icon="fa-trash" />
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoUploadInput',
  props: {
    label: {
      type: String,
      default: 'Carregue novos vídeos para sua coleção'
    },
    accept: {
      type: String,
      default: '.mp4, .avi'
    },
    acceptText: {
      type: String,
      default: 'Formatos aceitos conforme as regras do sistema.'
    },
    placeholder: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    previewUrl: {
      type: String,
      default: null
    },
    showInput: {
      type: Boolean,
      default: false
    },
    uploading: {
      type: Boolean,
      default: false
    },
    progress: {
      type: Number,
      default: 0
    },
    fileName: {
      type: String,
      default: ''
    },
    fileSize: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      inputId: `video-upload-${Math.random().toString(36).substr(2, 9)}`
    };
  },
  computed: {
    hasFile() {
      return Boolean(this.previewUrl || this.fileName);
    },
    displayName() {
      return this.fileName || 'Vídeo selecionado';
    },
    displayMeta() {
      const extension = this.fileExtension;
      const size = this.formattedSize;
      if (size && extension) return `${size} • ${extension}`;
      return size || extension || 'Arquivo de vídeo';
    },
    fileExtension() {
      const name = this.fileName || '';
      const match = name.match(/\.([a-z0-9]+)$/i);
      return match ? match[1].toUpperCase() : '';
    },
    formattedSize() {
      const bytes = Number(this.fileSize);
      if (!Number.isFinite(bytes) || bytes <= 0) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
      if (bytes < 1024 * 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    },
    safeProgress() {
      return Math.min(100, Math.max(0, Number(this.progress) || 0));
    },
    showProgress() {
      return this.uploading || this.safeProgress > 0;
    },
    isComplete() {
      return this.safeProgress >= 100 && !this.uploading;
    },
    progressWidth() {
      return `${this.safeProgress}%`;
    },
    progressPercent() {
      return `${Math.round(this.safeProgress)}%`;
    }
  },
  methods: {
    handleFileChange(event) {
      const file = event.target.files && event.target.files[0];
      if (file) {
        this.$emit('file-selected', file, event);
      }
    },
    onDrop(event) {
      if (this.disabled) return;
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (!file) return;
      this.$emit('file-selected', file, { target: { files: [file] } });
    },
    removePreview() {
      this.$emit('remove-preview');
    }
  }
};
</script>

<style scoped>
.video-upload-input {
  width: 100%;
}

.video-upload-input__zone {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 168px;
  gap: 6px;
  padding: 24px;
  text-align: center;
  border: 1.5px dashed #b9adf9;
  border-radius: 10px;
  background: #fbfaff;
  cursor: pointer;
  transition: border-color 150ms ease, background-color 150ms ease;
}

.video-upload-input__zone:hover {
  border-color: #4327c9;
  background: #f5f2ff;
}

.video-upload-input__file {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.video-upload-input__file:disabled {
  cursor: not-allowed;
}

.video-upload-input__zone:has(.video-upload-input__file:disabled) {
  opacity: 0.6;
  pointer-events: none;
}

.video-upload-input__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  margin-bottom: 4px;
  border-radius: 12px;
  background: #efebff;
  color: #5035d9;
  font-size: 18px;
}

.video-upload-input__title {
  font-size: 14px;
  font-weight: 700;
  color: #11143d;
}

.video-upload-input__hint,
.video-upload-input__formats {
  font-size: 12px;
  color: #64748b;
}

.video-upload-input__formats {
  font-size: 11px;
  color: #8490a5;
}

.video-upload-input__filecard {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  padding: 12px;
  border: 1px solid #e7eaf2;
  border-radius: 10px;
  background: #fff;
}

.video-upload-input__thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #11143d;
  color: #fff;
}

.video-upload-input__meta {
  min-width: 0;
  flex: 1;
}

.video-upload-input__name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #11143d;
}

.video-upload-input__details,
.video-upload-input__status {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
}

.video-upload-input__status--done {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #15803d;
  font-weight: 600;
}

.video-upload-input__progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}

.video-upload-input__bar {
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: #e7eaf2;
  overflow: hidden;
}

.video-upload-input__fill {
  height: 100%;
  border-radius: 999px;
  background: #4327c9;
}

.video-upload-input__percent {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #11143d;
}

.video-upload-input__remove {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: #fff1f2;
  color: #e11d48;
  cursor: pointer;
}

.video-upload-input__remove:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
